import React, { useEffect, useRef, useState } from "react";

export interface EditableTitleProps {
  value: string;
  onSave: (name: string) => Promise<void>;
  /** Shown while the name has not loaded yet. */
  placeholder?: string;
}

/**
 * The project's name, edited in place.
 *
 * Click-to-edit rather than a separate settings field: the title is already on
 * screen, and a rename is a small enough act that sending someone somewhere
 * else to do it is the wrong shape. Enter commits, Escape abandons, and blur
 * commits — a name half-typed and clicked away from is far more likely to be
 * finished than discarded.
 *
 * Empty input is rejected rather than saved: a project with no name is
 * unfindable in the list, and the old name is the better guess at intent.
 */
export const EditableTitle: React.FC<EditableTitleProps> = ({ value, onSave, placeholder = "Untitled" }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  const commit = async () => {
    const name = draft.trim();
    if (!name || name === value) {
      setDraft(value);
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(name);
      setEditing(false);
    } catch {
      // Stay in the field with the text intact rather than silently reverting.
      setDraft(name);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        title="Rename"
        className="min-w-0 truncate rounded-[4px] px-1 py-0.5 font-medium text-ink-100 transition-colors hover:bg-bay-800"
      >
        {value || placeholder}
      </button>
    );
  }

  return (
    <input
      ref={inputRef}
      value={draft}
      disabled={saving}
      maxLength={120}
      aria-label="Project name"
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          void commit();
        } else if (event.key === "Escape") {
          event.preventDefault();
          setDraft(value);
          setEditing(false);
        }
      }}
      className="min-w-0 max-w-[420px] flex-1 rounded-[4px] border border-[var(--edge-strong)] bg-bay-850 px-1 py-0.5 text-[13px] font-medium text-ink-100 outline-none focus:border-[var(--edge-focus)]"
    />
  );
};
